import {AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {TorchFilesService} from '@backend/modules/@files/services/torch-files.service';
import {UploadedFile} from '@backend/modules/@files/models/uploaded-file.model';
import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {debounceTime, distinctUntilChanged, filter, map, pairwise, takeUntil, tap, throttleTime} from 'rxjs/operators';
import {fromEvent, ReplaySubject, timer} from 'rxjs';
import {FileInfoDialogComponent} from '../file-info-dialog/file-info-dialog.component';
import {MatDialog} from '@angular/material/dialog';

@Component({
  selector: 'app-user-files-list',
  templateUrl: './user-files-list.component.html',
  styleUrls: ['./user-files-list.component.scss']
})
export class UserFilesListComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('scroller') scroller: CdkVirtualScrollViewport;
  @ViewChild('searchInput', {static: true}) searchInput: ElementRef;

  @Output() totalFiles = new EventEmitter<number>();

  files: UploadedFile[] = [];
  currentPage = 0;
  loading = false;
  searchValue = null;

  private _totalFiles = 0;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private _username: string;
  private searchChanged: EventEmitter<any> = new EventEmitter<any>();

  constructor(private torchFilesService: TorchFilesService, private ngZone: NgZone, private matDialog: MatDialog) {
  }

  ngOnInit(): void {
    this.fetchFiles();
    this.totalFiles.emit(0);

    this.searchChanged.pipe(
      takeUntil(this.destroyed$),
    ).subscribe(() => {
      this.files = [];
      this.fetchFiles();
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.setupScroller();

    fromEvent(this.searchInput.nativeElement, 'keyup').pipe(
      takeUntil(this.destroyed$),
      filter(Boolean),
      debounceTime(150),
      distinctUntilChanged(),
      tap(() => {
        const searchInputValue: string | null = this.searchInput.nativeElement.value;

        if (searchInputValue && searchInputValue.length) {
          this.searchValue = this.searchInput.nativeElement.value;
        } else {
          this.searchValue = null;
        }

        this.searchChanged.emit(this.searchValue);
      })
    ).subscribe();
  }

  @Input()
  set username(newValue: string) {
    this._username = newValue;
  }

  showFileInfo(file: UploadedFile) {
    const fileInfoDialog = this.matDialog.open(FileInfoDialogComponent, {
      data: file,
      panelClass: 'file-info-dialog'
    });

    fileInfoDialog.afterClosed().subscribe(result => {
      if (!!result && result === 'delete') {
        this.torchFilesService.deleteFile(file).subscribe();
      }
    });
  }

  get searchFilters() {
    if (!!this.searchValue && this.searchValue.length > 0) {

      return ['originalFileName', 'fileName'].map(columnName => `${columnName}||$contL||${this.searchValue}`);
    }

    return null;
  }


  private setupScroller() {
    this.scroller.elementScrolled().pipe(
      takeUntil(this.destroyed$),
      map(() => this.scroller.measureScrollOffset('bottom')),
      pairwise(),
      filter(([y1, y2]) => (y2 < y1 && y2 < 140)),
      throttleTime(200)
    ).subscribe(() => {
        if (this.files.length < this._totalFiles) {
          this.currentPage += 1;
          this.ngZone.run(() => {
            this.fetchFiles(this.currentPage);
          });
        }
      }
    );
  }

  private fetchFiles(currentPage = 0) {
    this.loading = true;

    let filters = ['uploadedBy||$eq||' + this._username];

    if (!!this.searchValue && !!this.searchFilters) {
      filters = [...filters, ...this.searchFilters];
    }


    this.torchFilesService.queryFiles(15, currentPage, null, 'id', filters, null).pipe(
      takeUntil(this.destroyed$),
    ).subscribe(res => {
      if (!!this.searchValue) {
        this.files = [...new Set([...this.files, ...res.data])];
        this.loading = false;
      } else if (res.total > 0) {
        this._totalFiles = res.total;
        this.totalFiles.next(this._totalFiles);
        timer(1000).subscribe(() => {
          this.loading = false;
          this.files = [...this.files, ...res.data];
        });
      } else {
        this.loading = false;
      }
    });
  }
}
